import * as React from 'react';
import { AddOn, AddOnId, AddOnCategory } from './types';
import { l } from '../localize';

type AddOnsListProps = {
	items: AddOn[];
	filterBy?: string;
};

const columnsForWidth = (width: number) => {
	return width > 1280 ? 6 : width > 1024 ? 4 : width > 640 ? 2 : 1;
};

const getExpandedPosition = (
	expandedAt: number | false,
	totalItems: number,
	columns: number,
) => {
	if (expandedAt === false) return -1;

	let position = columns - 1;

	if (columns === 1) {
		position = expandedAt;
	} else if (expandedAt > 0) {
		position = Math.ceil((expandedAt + 1) / columns) * columns - 1;
	}

	if (position >= totalItems) {
		position = totalItems - 1;
	}

	return position;
};

const categoryColorClassName = (category: AddOnCategory | false = false) => {
	return `category-${(category && category.color) || 'none'}`;
};

const AddOnsList: React.FC<AddOnsListProps> = ({ items }) => {
	const [expanded, setExpanded] = React.useState<AddOnId | false>(false);
	const [columns, setColumns] = React.useState(
		columnsForWidth(window.innerWidth),
	);

	const expandedAt = expanded && items.findIndex(item => item.id === expanded);
	const expandedItem = expanded && items.find(item => item.id === expanded);

	const expandedPosition = getExpandedPosition(
		expandedAt,
		items.length,
		columns,
	);

	React.useEffect(() => {
		const onResize = () => setColumns(columnsForWidth(window.innerWidth));
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, []);

	return (
		<div className="events">
			<div className="event-list grid clearfix">
				{items.map((item, i) => (
					<React.Fragment key={item.id}>
						<div
							className={`teaser grid-item ${
								expanded === item.id ? ' open' : ''
							}`}
						>
							<div className={categoryColorClassName(item.category)}>
								<div
									className="wrapper clearfix"
									onClick={() => {
										setExpanded(item.id);
									}}
									onKeyDown={ev => {
										if (ev.key === 'Enter') setExpanded(item.id);
									}}
									tabIndex={0}
									style={{ cursor: 'pointer' }}
								>
									<div className="grid-image">
										{item.image && <img src={item.image} alt="" />}
									</div>
									<div className="grid-info">
										<h2>{item.title}</h2>
										{item.date && item.date.daily && (
											<span className="date">{l('daily')}</span>
										)}
									</div>
								</div>
							</div>
						</div>
						{expandedItem && expandedPosition === i && (
							<AddOnExpanded
								item={expandedItem}
								onCloseClick={() => setExpanded(false)}
							/>
						)}
					</React.Fragment>
				))}
			</div>
		</div>
	);
};

export default AddOnsList;

/**
 * Private components
 */

type AddOnExpandedProps = {
	item: AddOn;
	onCloseClick: () => void;
};

const AddOnExpanded: React.FC<AddOnExpandedProps> = ({
	item,
	onCloseClick,
}) => {
	const el = React.createRef<HTMLDivElement>();
	React.useEffect(() => {
		if (!el.current) return;
		const rect = el.current.getBoundingClientRect();
		if (rect.top > window.innerHeight / 2) {
			window.scrollTo({
				left: 0,
				top: rect.top + window.scrollY - 155, // 155 is the height of the header
				behavior: 'smooth',
			});
		}
	}, [item.id]);

	return (
		<div className="open-article-space" ref={el}>
			<div>
				<div className={`clearfix ${categoryColorClassName(item.category)}`}>
					<div className="image">
						{item.image_large && <img alt="" src={item.image_large} />}
					</div>
					<div className="info">
						<h2>{item.title}</h2>
						{item.sub_title && <h3>{item.sub_title}</h3>}

						{item.date && (
							<span className="date">{item.date.daily && l('daily')}</span>
						)}

						<div
							className="body"
							dangerouslySetInnerHTML={{ __html: item.description }}
						/>
						<div className="links">
							{item.map_link && <MapLink item={item} />}
							{item.phone && <PhoneLink item={item} />}
							{item.email && <EmailLink item={item} />}
						</div>

						{item.partner_link && <PartnerLink item={item} />}
					</div>
					<button className="close-btn" onClick={onCloseClick}>
						<span>×</span>
					</button>
				</div>
			</div>
		</div>
	);
};

type LinkProps = { item: AddOn };

const MapLink: React.FC<LinkProps> = ({ item }) => (
	<a href={item.map_link} target="_blank" rel="noopener" className="map-link">
		{l('map')}
	</a>
);

const PhoneLink: React.FC<LinkProps> = ({ item }) => (
	<a href={`tel:${item.phone}`} className="phone-link">
		{item.phone}
	</a>
);

const EmailLink: React.FC<LinkProps> = ({ item }) => (
	<a href={`mailto:${item.email}`} className="email-link">
		{item.email}
	</a>
);

const PartnerLink: React.FC<LinkProps> = ({ item }) => {
	if (!item.partner_link.url) return <></>;

	return (
		<div className="outlink">
			<a href={`${item.partner_link.url}`} target="_blank" rel="noopener">
				{item.partner_link.title || l('readMore')}
			</a>
		</div>
	);
};
